import React, { useCallback, useEffect, useState, useRef } from "react";
import CustomLayout from "../../Core/LayoutV2/CustomLayout";
import ChatCard from "./ChatCard";
import CustomSearchBox from "../../Core/CommonV2/CustomSearchBox";
import { PlusIcon } from "../../Core/svgV2/PlusIcon";
import { Dropdown } from "antd";
import { ThreadIcon } from "../../Core/svgV2/ThreadIcon";
import ChatWindow from "./ChatWindow";
import { useAppDispatch, useAppSelector } from "../../Core/redux/hooks";
import {
  resetConversations,
  updateConversations,
  updateConversationsMultiple,
  updateThreadMessage,
  updateUnreadCountForProjectThreads,
} from "../../Core/redux/slices/conversations.slice";
import DeleteGroupModal from "./Modals/DeleteGroupModal";
import ShareModal from "./Modals/ShareModal";
import SaveInLibrary from "./Modals/SaveInLibrary";
import GroupDetailsDrawer from "./Drawers/GroupDetailsDrawer";
import UserDetailsDrawer from "./Drawers/UserDetailsDrawer";
import SharedFilesDrawer, {
  SharedFileDetailsDrawer,
} from "./Drawers/SharedFilesDrawer";
// import FilePreviweModal from "./Modals/FIlePreviweModal";
import ConversationFilePreviewModal from "./Modals/ConversationFilePreviewModal";
import ConversationsAppTour from "./ConversationsAppTour";
import { getThreads } from "../../Core/redux/api/conversationAPI";
import DeleteFileModal from "./Modals/DeleteFileModal";
import LeaveGroupModal from "./Modals/LeaveGroupModal";
import { playNotifySoundDebounced } from "../../Core/utils";
import { getAllTourStatus } from "../../Core/redux/api/dashboardAPI";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import ForwardMessageModal from "../../ModulesV3/Conversations/Modals/ForwardmessageModal";
import CreateGroupModal from "../../ModulesV3/Conversations/Modals/CreateGroupModal";
import EditGroupModal from "../../ModulesV3/Conversations/Components/Modals/EditGroupModal";
import AddGroupMembersModal from "./Modals/AddGroupMembersModal";

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatLastTime = (ts: string | null | undefined): string => {
  if (!ts) return "";
  const d = dayjs(ts);
  if (!d.isValid()) return "";
  if (d.isSame(dayjs(), "day")) return d.local().format("h:mm A");
  if (d.isSame(dayjs().subtract(1, "day"), "day")) return "Yesterday";
  return d.local().format("D MMM");
};

// ── Sidebar view state ────────────────────────────────────────────────────────
// "all"    → Quick Actions + "Recent chats" header (default)
// "direct" → back arrow + "Direct Messages" header, DM threads only
// "groups" → back arrow + "Groups" header, group threads only
type SidebarView = "all" | "direct" | "groups";

// ── Back Arrow Icon ───────────────────────────────────────────────────────────
const BackArrowIcon = ({ className = "" }: { className?: string }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 12.5L5.5 8L10 3.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ── Quick Actions ─────────────────────────────────────────────────────────────
const QuickActions = ({
  threads,
  onSelect,
}: {
  threads: any[];
  onSelect: (view: SidebarView) => void;
}) => {
  const [open, setOpen] = useState(true);

  const directUnread = threads
    .filter((t) => t.isGroup === false)
    .reduce((sum, t) => sum + (+t.unreadCount || 0), 0);

  const groupUnread = threads
    .filter((t) => t.isGroup === true)
    .reduce((sum, t) => sum + (+t.unreadCount || 0), 0);

  const requestsPending = 0;

  const cards: {
    key: SidebarView | "requests";
    label: string;
    sub: string;
    dot: boolean;
    disabled?: boolean;
  }[] = [
      { key: "requests", label: "Requests", sub: `${requestsPending} pending`, dot: requestsPending > 0, disabled: true },
      { key: "direct", label: "Direct", sub: `${directUnread} unread`, dot: directUnread > 0 },
      { key: "groups", label: "Groups", sub: `${groupUnread} unread`, dot: groupUnread > 0 },
    ];

  return (
    <div className="w-full">
      {/* Header */}
      <button
        className="flex w-full items-center justify-between pl-6 pr-2 py-3"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="out-500-14" style={{ color: "#292927" }}>Quick Actions</span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${open ? "" : "rotate-180"}`}
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M4 10L8 6l4 4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className="flex gap-x-1 pl-6 pr-2 pb-4">
          {cards.map((c) => (
            <button
              key={c.label}
              disabled={c.disabled}
              onClick={() => !c.disabled && onSelect(c.key as SidebarView)}
              className={`relative flex-1 min-w-0 h-[72px] overflow-hidden flex flex-col items-start gap-y-1 border border-gray-200 rounded-[10px] p-4 bg-[#F9FBFE] transition-colors ${c.disabled ? "cursor-not-allowed opacity-60" : "hover:bg-[#F0F5FA]"
                }`}
            >
              {c.dot && (
                <span className="absolute top-5 right-2 w-1.5 h-1.5 rounded-full bg-[#165ABF]" />
              )}
              <span className="out-500-14 leading-[20px] w-full truncate" style={{ color: "#292927" }}>{c.label}</span>
              <span className="text-[12px] leading-[18px] text-gray-500 w-full truncate">{c.sub}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const ConversationsScreen = ({ socket }) => {
  const { user } = useAppSelector((state) => state.userDetails);
  const { tenantDetails } = useAppSelector((state) => state.tenant);
  const { allowedUsersForGuest } = useAppSelector((state) => state.settings);
  const navigate = useNavigate();

  const {
    threads,
    userDetailsDrawer,
    groupDetailsDrawer,
    leaveThread,
    sharedFilesDrawer,
    sharedFileDetailsDrawer,
    shareMessageModal,
    saveInLibrary,
    filePreview,
    deleteFileModal,
    currentThread,
    editThread,
    editThreadDetails,
  } = useAppSelector((state) => state.conversations);
  const dispatch = useAppDispatch();

  const [search, setSearch] = useState("");
  const [sidebarView, setSidebarView] = useState<SidebarView>("all");

  const getDM = async () => {
    await dispatch(getThreads({ userId: user.id, tenantId: tenantDetails.tenantId }));
  };
  const tourStatus = async () => {
    await dispatch(getAllTourStatus({ userId: user.id }));
  };
  useEffect(() => {
    if (!user.id || !tenantDetails?.tenantId) return;
    getDM();
    tourStatus();
  }, [user.id, tenantDetails?.tenantId]);

  const allThreads: any[] = threads || [];
  // if (+user.roleId === 4) {
  //   const allowedSet = new Set((allowedUsersForGuest || []).map(String));
  //   console.log('[GuestFilter] allowedUsersForGuest:', allowedUsersForGuest);
  //   allThreads = allThreads.filter((t) =>
  //     t.isGroup === false ? allowedSet.has(String(t.userid ?? t.userId)) : true
  //   );
  //   console.log('[GuestFilter] allThreads after filter:', allThreads.length, 'of', threads.length);
  // }

  // add near your other refs/state
  const searchInputRef = useRef<HTMLInputElement>(null);

  // register the shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isModifierPressed = e.metaKey || e.ctrlKey; // Cmd on mac, Ctrl elsewhere
      if (isModifierPressed && e.key.toLowerCase() === "k") {
        e.preventDefault(); // stops the browser's own Ctrl+K (address bar search in some browsers)
        searchInputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const viewFilteredThreads =
    sidebarView === "direct"
      ? allThreads.filter((t) => t.isGroup === false)
      : sidebarView === "groups"
        ? allThreads.filter((t) => t.isGroup === true)
        : allThreads;

  const handleSort = (list: any[]) =>
    [...list].sort((a, b) => {
      const aTime = new Date(a.lastMessageUpdate || a.lastMessageTime || 0).getTime();
      const bTime = new Date(b.lastMessageUpdate || b.lastMessageTime || 0).getTime();
      return bTime - aTime;
    });

  const handleSearch = useCallback(
    (list: any[]) => {
      if (!search.trim()) return list;
      const regex = new RegExp(search, "i");
      return list.filter(
        (t) =>
          regex.test(t.name) ||
          regex.test(t.threadName) ||
          regex.test(t.firstname) ||
          regex.test(t.lastname)
      );
    },
    [search]
  );

  const sortThreads = useCallback(
    (list: any[]) => {
      const pinned = handleSort(list.filter((t) => t.isPinned == 1));
      const unpinned = handleSort(list.filter((t) => t.isPinned != 1));
      return [...pinned, ...unpinned];
    },
    [threads]
  );

  useEffect(() => {
    if (+user.roleId === 4 && user.isGuestDeactivated) navigate("/");
  }, []);

  const displayThreads = sortThreads(handleSearch(viewFilteredThreads));

  const sidebarHeaderLabel =
    sidebarView === "direct" ? "Direct Messages" : sidebarView === "groups" ? "Groups" : "Recent chats";
  return (
    <CustomLayout
      page="messages"
      title={<div className="text-black out-600-14 font-[600]">Messages</div>}    >
      <ConversationsAppTour />


      <div className="relative flex h-full bg-white">
        {/* ── Sidebar ─────────────────────────────────────────────────── */}
        <div
          className="w-[320px] min-w-[320px] max-w-[320px] border-r border-gray-200 flex flex-col overflow-hidden"
          id="conversations-tour-messages"
        >
          {/* Search row */}
          <div className="flex gap-x-3 py-2 h-[46px] px-4 border-b border-gray-200 flex-shrink-0 items-center">
            <div className="flex flex-1 items-center gap-x-[6px] bg-[#F0F5FA] border border-gray-200 rounded-[8px] px-[10px] h-[36px]">
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" viewBox="0 0 16 16" fill="none">
                <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M11 11l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                ref={searchInputRef}
                className="flex-1 bg-transparent text-[14px] text-gray-700 placeholder-gray-400 outline-none leading-[20px]"
                placeholder="Search in messages"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <Dropdown
              overlayClassName="new-group-dropdown"
              menu={{
                items: [
                  {
                    key: "new-group",
                    label: (
                      <div
                        className="text-gray-700 out-300-14"
                        onClick={() =>
                          dispatch(
                            updateConversationsMultiple([
                              { key: "newGroupModal", value: true },
                              { key: "editThread", value: false },
                            ])
                          )
                        }
                      >
                        Create new group
                      </div>
                    ),
                    icon: <ThreadIcon />,
                  },
                ],
              }}
              trigger={["click"]}
            >
              <button className="flex-shrink-0 flex items-center justify-center">
                <PlusIcon className="text-gray-500" />
              </button>
            </Dropdown>
            <style>{`
 .new-group-dropdown .ant-dropdown-menu {
  width: 200px !important;
  height: 45px !important;
  padding: 2.5px !important;
  border: 1px solid #EDF3F9 !important;
  border-radius: 8px !important;
  box-sizing: border-box !important;
}
.new-group-dropdown .ant-dropdown-menu-item {
  width: 100% !important;
  height: 40px !important;
  display: flex !important;
  align-items: center !important;
  box-sizing: border-box !important;
  margin: 0 !important;
}
  }
`}</style>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto conv-sidebar-scroll w-full">
            {sidebarView === "all" ? (
              <>
                <QuickActions threads={allThreads} onSelect={setSidebarView} />

                <div className="px-6 pt-1 pb-2">
                  <span className="out-500-14" style={{ color: "#292927" }}>{sidebarHeaderLabel}</span>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-x-2 px-6 py-3">
                <button
                  onClick={() => setSidebarView("all")}
                  className="flex items-center justify-center text-gray-500 hover:text-gray-700"
                >
                  <BackArrowIcon className="w-4 h-4" />
                </button>
                <span className="out-500-14 text-gray-900">{sidebarHeaderLabel}</span>
              </div>
            )}

            {/* Chat list */}
            {displayThreads.map((thread, i) => (
              <ChatCard
                key={thread.threadId || thread.id || i}
                thread={thread}
                socket={socket}
                formatTime={formatLastTime}
              />
            ))}
          </div>
        </div>

        {/* ── Chat window ─────────────────────────────────────────────── */}
        <div className="flex-1 min-h-0 h-full overflow-hidden">
          <ChatWindow socket={socket} />
        </div>
        <ConversationFilePreviewModal socket={socket} />
      </div>

      {/* Modals & Drawers */}
      <CreateGroupModal
        onClose={() => dispatch(resetConversations(["newGroupModal", "editThread", "editThreadDetails"]))}
      />

      <AddGroupMembersModal />

      {/* <FilePreviweModal
        visible={filePreview.visible}
        page="messages"
        onClose={() => dispatch(resetConversations(["filePreview"]))}
      /> */}
      {/* <ConversationFilePreviewModal /> */}
      <SharedFilesDrawer
        visible={sharedFilesDrawer}
        onClose={() => dispatch(resetConversations(["sharedFilesDrawer"]))}
      />
      <SaveInLibrary
        visible={saveInLibrary.visible}
        onClose={() => dispatch(resetConversations(["saveInLibrary"]))}
      />
      <SharedFileDetailsDrawer
        visible={sharedFileDetailsDrawer.visible}
        data={sharedFileDetailsDrawer.data}
        onClose={() => dispatch(resetConversations(["sharedFileDetailsDrawer"]))}
      />
      <DeleteFileModal
        visible={deleteFileModal}
        onClose={() =>
          dispatch(resetConversations(["deleteFileModal", "deleteFileInMessage"]))
        }
      />
      <LeaveGroupModal />
      <EditGroupModal
        visible={!!editThread}
        groupId={editThreadDetails?.threadId}
        groupName={editThreadDetails?.name ?? editThreadDetails?.threadName ?? ""}
        groupImage={editThreadDetails?.groupProfilePicture || editThreadDetails?.profilePicture}
        onCloseCallback={() =>
          dispatch(
            updateConversationsMultiple([
              { key: "editThread", value: false },
              { key: "editThreadDetails", value: {} },
              { key: "newGroupModal", value: false }, // ← guard against stale flag
            ])
          )
        }
      />
      <DeleteGroupModal />
      <ForwardMessageModal socket={socket} />
      <GroupDetailsDrawer
        open={groupDetailsDrawer.visible}
        details={groupDetailsDrawer.data}
        onClose={() =>
          dispatch(
            updateConversations({ key: "groupDetailsDrawer", value: { visible: false, data: {} } })
          )
        }
      />
      <UserDetailsDrawer
        open={userDetailsDrawer.visible}
        user={userDetailsDrawer.data}
        onClose={() =>
          dispatch(
            updateConversations({ key: "userDetailsDrawer", value: { visible: false, data: {} } })
          )
        }
      />

    </CustomLayout>
  );
};

export default ConversationsScreen;